import React, { useEffect, useState } from 'react';
import { BuildingIcon, ChevronRightIcon, PlusIcon, UserIcon, WrenchIcon } from 'lucide-react';
import { BillingType, CompanySettings, Employee, JobStatus, ProCustomer, ProJob } from '../../types/pro';
import { Modal } from './Modal';
import { CustomerPicker } from './invoice/CustomerPicker';
interface NewJobModalProps {
  isOpen: boolean;
  onClose: () => void;
  customers: ProCustomer[];
  employees: Employee[];
  company: CompanySettings;
  nextJobNo: string;
  /** Pre-selects the customer when opened from a customer's page. */
  presetCustomerId?: string;
  onCreateCustomer: () => void;
  onSave: (job: Omit<ProJob, 'id'>) => void;
}
const field = 'w-full rounded-lg border border-stone-200 px-3 py-2.5 text-sm outline-none focus:border-transparent focus:ring-2 focus:ring-brand-500';
const label = 'mb-1.5 block text-sm font-medium text-stone-700';
const ghost = 'rounded-lg border border-stone-200 bg-white px-4 py-2.5 text-sm font-semibold text-stone-700 transition-colors hover:bg-stone-100';
const primary = 'rounded-lg bg-brand-600 px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-brand-700 disabled:bg-stone-200 disabled:text-stone-400';

/** Common jobs, so the usual ones are a single tap. */
const PRESETS = ['Fejlfinding', 'Servicebesøg', 'Installation af ladeboks', 'Udskiftning af eltavle', 'Nye stikkontakter', 'Belysning'];
function todayValue(): string {
  const now = new Date();
  const pad = (value: number) => String(value).padStart(2, '0');
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
}

/**
 * Most work for an electrician never has a quote — the phone rings, you drive
 * out, and you bill the hours afterwards. This creates that job directly,
 * with only the customer and a description actually required.
 */
export function NewJobModal({
  isOpen,
  onClose,
  customers,
  employees,
  company,
  nextJobNo,
  presetCustomerId,
  onCreateCustomer,
  onSave
}: NewJobModalProps) {
  const [customerId, setCustomerId] = useState('');
  const [picking, setPicking] = useState(false);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [address, setAddress] = useState('');
  const [city, setCity] = useState('');
  const [billingType, setBillingType] = useState<BillingType>('regning');
  const [fixedPrice, setFixedPrice] = useState('');
  const [rate, setRate] = useState('');
  const [estimatedHours, setEstimatedHours] = useState('2');
  const [employeeId, setEmployeeId] = useState('');
  const [schedule, setSchedule] = useState<'nu' | 'dato' | 'senere'>('nu');
  const [date, setDate] = useState(todayValue());
  const [time, setTime] = useState('08:00');
  const customer = customers.find(item => item.id === customerId);
  useEffect(() => {
    if (!isOpen) return;
    const preset = customers.find(item => item.id === presetCustomerId);
    setCustomerId(preset?.id ?? '');
    setAddress(preset?.address ?? '');
    setCity(preset?.city ?? '');
    setPicking(!preset);
    setTitle('');
    setDescription('');
    setBillingType('regning');
    setFixedPrice('');
    setRate('');
    setEstimatedHours('2');
    setEmployeeId(employees[0]?.id ?? '');
    setSchedule('nu');
    setDate(todayValue());
    setTime('08:00');
  }, [isOpen, presetCustomerId, customers, employees]);
  const pick = (picked: ProCustomer) => {
    setCustomerId(picked.id ?? '');
    setAddress(picked.address);
    setCity(picked.city);
  };
  const canSave = Boolean(customerId) && title.trim().length > 0;
  const submit = (startNow: boolean) => {
    if (!canSave) return;
    const hours = Number(estimatedHours.replace(',', '.')) || 0;
    const customRate = Number(rate.replace(',', '.'));
    const price = Number(fixedPrice.replace(',', '.')) || 0;
    let start: string | undefined;
    let status: JobStatus = 'ikke-planlagt';
    if (startNow) {
      start = new Date().toISOString();
      status = 'i-gang';
    } else if (schedule === 'nu') {
      start = new Date().toISOString();
      status = 'planlagt';
    } else if (schedule === 'dato') {
      start = new Date(`${date}T${time}`).toISOString();
      status = 'planlagt';
    }
    onSave({
      jobNo: nextJobNo,
      customerId,
      customerName: customer?.name ?? '',
      address: address.trim(),
      city: city.trim(),
      title: title.trim(),
      description: description.trim(),
      status,
      billingType,
      ...(billingType === 'fast-pris' ? {
        fixedPrice: price
      } : {}),
      ...(start ? {
        start
      } : {}),
      durationHours: hours > 0 ? hours : 2,
      employeeIds: employeeId ? [employeeId] : [],
      estimatedHours: hours,
      hourlyRate: billingType === 'regning' && customRate > 0 ? customRate : null,
      materials: [],
      photos: [],
      documents: [],
      notes: ''
    });
    onClose();
  };
  return <>
      <Modal isOpen={isOpen && !picking} onClose={onClose} title="Ny opgave" subtitle={`${nextJobNo} · uden tilbud`} size="lg" footer={<>
            <button onClick={onClose} className={ghost} data-id="element-460" data-mp="1r7xq4a9v4k3w">
              Annuller
            </button>
            <button onClick={() => submit(true)} disabled={!canSave} className={ghost} data-id="element-461" data-mp="1w8v9uraitm7i">
              Opret og start nu
            </button>
            <button onClick={() => submit(false)} disabled={!canSave} className={primary} data-id="element-462" data-mp="13jxtitpvdzhe">
              Opret opgave
            </button>
          </>} data-id="element-459" data-mp="1wdb2hnn0g31u">
        <div className="space-y-5" data-id="element-463" data-mp="1dfrd2x20at8o">
          {/* Customer */}
          <div data-id="element-464" data-mp="1i8kizo97o7mc">
            <span className={label} data-id="element-465" data-mp="1i93zr1n4kpys">Kunde</span>
            {customer ? <button onClick={() => setPicking(true)} className="flex w-full items-center gap-3 rounded-lg border border-stone-200 px-3.5 py-3 text-left transition-colors hover:bg-stone-50" data-id="element-466" data-mp="kt6mhytcul66">
                {customer.type === 'firma' ? <BuildingIcon className="h-4 w-4 flex-shrink-0 text-stone-400" data-id="element-467" data-mp="cs8q7zkqms05" /> : <UserIcon className="h-4 w-4 flex-shrink-0 text-stone-400" data-id="element-468" data-mp="rglo523dykxa" />}
                <span className="min-w-0 flex-1" data-id="element-469" data-mp="mi21t0kvr74e">
                  <span className="block truncate text-sm font-semibold text-stone-900" data-id="element-470" data-mp="miadwq96i8ej">
                    {customer.name}
                  </span>
                  <span className="block truncate text-xs text-stone-500" data-id="element-471" data-mp="3pyqyluunv2gv">
                    {[customer.address, customer.postalCode, customer.city].filter(Boolean).join(' ')}
                  </span>
                </span>
                <span className="flex-shrink-0 text-xs font-medium text-brand-700" data-id="element-472" data-mp="3tdgwemed1sm">
                  Skift
                </span>
              </button> : <button onClick={() => setPicking(true)} className="flex w-full items-center gap-3 rounded-lg border border-dashed border-stone-300 px-3.5 py-3 text-left transition-colors hover:border-brand-400 hover:bg-brand-50" data-id="element-473" data-mp="1f5xvdwf5kiwy">
                <PlusIcon className="h-4 w-4 flex-shrink-0 text-stone-400" data-id="element-474" data-mp="1dq24adsjee77" />
                <span className="flex-1 text-sm text-stone-500" data-id="element-475" data-mp="1hqm1fhd41htc">
                  Vælg kunde
                </span>
                <ChevronRightIcon className="h-4 w-4 text-stone-300" data-id="element-476" data-mp="25g6pgeo4f59h" />
              </button>}
          </div>

          {/* What the job is */}
          <div data-id="element-477" data-mp="2ahdwgbifw0f3">
            <label className="block" data-id="element-478" data-mp="1wo4totozyd5j">
              <span className={label} data-id="element-479" data-mp="1wn3bm59kcqeb">Opgave</span>
              <input value={title} onChange={e => setTitle(e.target.value)} placeholder="f.eks. Fejlfinding på belysning" className={field} data-id="element-480" data-mp="20pqagq4njbl0" />
            </label>
            <div className="mt-2 flex flex-wrap gap-1.5" data-id="element-481" data-mp="2t72mprbciso4">
              {PRESETS.map(preset => <button key={preset} onClick={() => setTitle(preset)} className="rounded-md bg-stone-100 px-2.5 py-1 text-xs font-medium text-stone-600 transition-colors hover:bg-stone-200" data-id="element-482" data-mp="24gg5u5r2l7tn">
                  {preset}
                </button>)}
            </div>
          </div>

          <label className="block" data-id="element-483" data-mp="2ndz7upf41q6k">
            <span className={label} data-id="element-484" data-mp="2nd7kg1vo2yrd">Beskrivelse</span>
            <textarea value={description} onChange={e => setDescription(e.target.value)} rows={3} placeholder="Hvad skal der laves? Aftaler med kunden, adgangsforhold…" className={`${field} leading-relaxed`} data-id="element-485" data-mp="2if64vqxg9hkn" />
          </label>

          {/* Address — copied from the customer but editable, since work is
              often done somewhere other than the billing address. */}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3" data-id="element-486" data-mp="x09ufjjqz7j0">
            <label className="block sm:col-span-2" data-id="element-487" data-mp="38py56smph3vd">
              <span className={label} data-id="element-488" data-mp="38n3mb5jcbr42">Arbejdsadresse</span>
              <input value={address} onChange={e => setAddress(e.target.value)} className={field} data-id="element-489" data-mp="3dpb1s53dppb5" />
            </label>
            <label className="block" data-id="element-490" data-mp="3necgzt23o2o2">
              <span className={label} data-id="element-491" data-mp="3nfdstzqxwt6u">By</span>
              <input value={city} onChange={e => setCity(e.target.value)} className={field} data-id="element-492" data-mp="3sdfkh92fm0tx" />
            </label>
          </div>

          {/* Pricing */}
          <div className="rounded-lg border border-stone-200 p-4" data-id="element-493" data-mp="586f5js4qtzt">
            <span className={label} data-id="element-494" data-mp="a7j182b6fx1c">Afregning</span>
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2" data-id="element-495" data-mp="a70yqnxx8mkz">
              {[{
              id: 'regning' as const,
              title: 'Regningsarbejde',
              detail: 'Timer og materialer efter forbrug'
            }, {
              id: 'fast-pris' as const,
              title: 'Fast pris',
              detail: 'Aftalt beløb uanset forbrug'
            }].map(option => <button key={option.id} onClick={() => setBillingType(option.id)} aria-pressed={billingType === option.id} className={`rounded-lg border p-3 text-left transition-colors ${billingType === option.id ? 'border-brand-500 bg-brand-50' : 'border-stone-200 hover:bg-stone-50'}`} data-id="element-496" data-mp="28yojfo9ai5qx">
                  <span className="block text-sm font-semibold text-stone-900" data-id="element-497" data-mp="2wpc3f7whcltu">
                    {option.title}
                  </span>
                  <span className="mt-0.5 block text-xs text-stone-500" data-id="element-498" data-mp="30s8iiomt9zt1">
                    {option.detail}
                  </span>
                </button>)}
            </div>

            <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2" data-id="element-499" data-mp="u3q03c9lpopo">
              {billingType === 'regning' ? <label className="block" data-id="element-500" data-mp="p20ptyu7df2n">
                  <span className={label} data-id="element-501" data-mp="l1yt843brbu8">Timepris</span>
                  <input value={rate} onChange={e => setRate(e.target.value)} inputMode="decimal" placeholder={String(company.hourlyRate)} className={field} data-id="element-502" data-mp="l2id3qcjcaaw" />
                  <span className="mt-1 block text-xs text-stone-400" data-id="element-503" data-mp="18sm7r87qwzpr">
                    Tom = firmaets {company.hourlyRate} kr./time
                  </span>
                </label> : <label className="block" data-id="element-504" data-mp="z0yklji824u3">
                  <span className={label} data-id="element-505" data-mp="yzx778dqh697">Aftalt pris ekskl. moms</span>
                  <input value={fixedPrice} onChange={e => setFixedPrice(e.target.value)} inputMode="decimal" placeholder="0" className={field} data-id="element-506" data-mp="1wioynkzxbzms" />
                </label>}
              <label className="block" data-id="element-507" data-mp="2fkz3f48u983q">
                <span className={label} data-id="element-508" data-mp="21ulv30s1brdv">Forventet tid</span>
                <div className="flex items-center gap-2" data-id="element-509" data-mp="21u3u5qufgozm">
                  <input value={estimatedHours} onChange={e => setEstimatedHours(e.target.value)} inputMode="decimal" className={field} data-id="element-510" data-mp="25v5te7och11l" />
                  <span className="flex-shrink-0 text-sm text-stone-500" data-id="element-511" data-mp="1wwkfwqxqcc7o">
                    timer
                  </span>
                </div>
              </label>
            </div>
          </div>

          {/* When */}
          <div data-id="element-512" data-mp="3d9j0hl8j0t7e">
            <span className={label} data-id="element-513" data-mp="3d9siomnbxw2j">Tidspunkt</span>
            <div className="flex flex-wrap gap-2" data-id="element-514" data-mp="3daakdysrh2hu">
              {[{
              id: 'nu' as const,
              label: 'I dag'
            }, {
              id: 'dato' as const,
              label: 'Vælg dato'
            }, {
              id: 'senere' as const,
              label: 'Planlægges senere'
            }].map(option => <button key={option.id} onClick={() => setSchedule(option.id)} aria-pressed={schedule === option.id} className={`rounded-lg px-3 py-2 text-sm font-medium transition-colors ${schedule === option.id ? 'bg-stone-900 text-white' : 'bg-stone-100 text-stone-600 hover:bg-stone-200'}`} data-id="element-515" data-mp="11nqmscl8xc8a">
                  {option.label}
                </button>)}
            </div>

            {schedule === 'dato' && <div className="mt-3 grid grid-cols-2 gap-3" data-id="element-516" data-mp="1tdv0xtiex312">
                <input type="date" value={date} onChange={e => setDate(e.target.value)} className={field} data-id="element-517" data-mp="3xvpg5rbn1i9" />
                <input type="time" value={time} onChange={e => setTime(e.target.value)} className={field} data-id="element-518" data-mp="1eiii7bv6m6n" />
              </div>}
          </div>

          {employees.length > 1 && <label className="block" data-id="element-519" data-mp="fdi75ezdn12t">
              <span className={label} data-id="element-520" data-mp="fcfhfadsj4oh">Ansvarlig</span>
              <select value={employeeId} onChange={e => setEmployeeId(e.target.value)} className={field} data-id="element-521" data-mp="3dvdwoykgs1nk">
                {employees.map(employee => <option key={employee.id} value={employee.id} data-id="element-522" data-mp="3nta8ztp512n9">
                    {employee.name}
                  </option>)}
              </select>
            </label>}

          {!canSave && <p className="flex items-center gap-2 text-xs text-stone-400" data-id="element-523" data-mp="2v43c0z5c0l44">
              <WrenchIcon className="h-3.5 w-3.5" data-id="element-524" data-mp="3037i3gwym2q8" />
              Vælg en kunde og skriv hvad opgaven er, så kan den oprettes.
            </p>}
        </div>
      </Modal>

      <CustomerPicker isOpen={isOpen && picking} onClose={() => setPicking(false)} customers={customers} onSelect={pick} onCreateNew={() => {
      setPicking(false);
      onCreateCustomer();
    }} data-id="element-525" data-mp="2nln17jlms5m4" />
    </>;
}